import React, { useState, useMemo, useEffect, useCallback, PropsWithChildren } from "react";
import { defineMessages, FormattedMessage, useIntl } from "react-intl";
import Form, {
  UiSchema,
  ISubmitEvent,
  Field,
  AjvError,
  IChangeEvent,
  FormValidation,
  ErrorSchema,
} from "react-jsonschema-form";
import { JSONSchema6 } from "json-schema";
import { Button, Alert, Card } from "react-bootstrap";
import { ArrayWidget, InputWidget, FormFieldWidget } from "../widgets";
import styled from "styled-components";
import { Identifiable } from "../context";
import { useCrudNavigation } from "./container";
import { useLoaderState } from "../hooks";
import ContentWrapper from "../../../components/content-wrapper";
import { faSyncAlt, faCircleNotch } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Message } from "../i18n";
import { SectionHeading } from "../../../components/section-heading";
import { IAuthzAction, useAuthorizer } from "../../../lib/action-authorizer";
import { useHistory } from "react-router";
import SimpleCrud, { CrudMessages } from "./simple-crud";
import cloneDeep from "lodash/cloneDeep";
import { v4 as uuidv4 } from "uuid";
import { TableColumns } from "./list";
import { APP_COLORS } from "../../witadmin/Common/constants";

export type FormConfiguration<T> = FormConfigurationFields<T> & {
  /**
   * Defines the order on which fields will be displayed in the form.
   */
  __fieldOrder?: (keyof T | "*")[];
  /**
   * Indicates that schema properties that are arrays must be appended the 'List' suffix.
   * This is needed to match protobuf generated messages properties with schema properties.
   *
   * If the form model is not generated from protobuf, this can be omitted.
   */
  __useProtoStyleArrays?: boolean;
};
export type FormConfigurationFields<T> = {
  [P in keyof T]?: FormFieldConfiguration<T[P]>;
};

export interface FormFieldConfiguration<T> {
  labelId?: string;
  hidden?: boolean | ((isNew: boolean) => boolean);
  hideTitle?: boolean;
  readonly?: boolean | ((isNew: boolean) => boolean);
  default?: T | (() => T);
  /**
   * If the form field is a enum, the labels to use for the values. Must be the same
   * length as the enum array.
   */
  enumLabels?: Message[];
  /**
   * transform can be used to add/complement values to a complex structure (e.g. translations)
   * parent transformations trump child ones. As they manipulate that whole subtree.
   */
  transform?: (originalValue: T) => T | undefined;
  /**
   * Configuration for nested fields (if this field is a nested object)
   */
  children?: T extends Array<any> ? FormConfiguration<T[0]> : FormConfiguration<T>;
  widget?: InputWidget | string;
  fieldWidget?: FormFieldWidget<T>;
  /**
   * For array fields only. Customizes how the array will be displayed.
   */
  displayArrayAs?: T extends Array<any> ? ArrayWidget<T> : undefined;
  /**
   * A help text to display for this field.
   */
  messages?: {
    helpText?: Message;
    description?: Message;
    label?: Message | Message[];
    patternValidationFailed?: Message;
  };
  placeholder?: Message;
  nestedFormConfiguration?: unknown;
  nestedTableColumns?: unknown;
  nestedEntityName?: Message;
  id?: string;
}

export interface FormMessages {
  loading: Message;
  loadRetry: Message;
  loadError: Message;
  entityName: Message;
  saveReminder: Message;
  saveError: Message;
  save: Message;
  saving: Message;
  goBack: Message;
  requiredValidationFailed: Message;
  minLengthValidationFailed: Message;
  maxLengthValidationFailed: Message;
  defaultPatternFailedMessage: Message;
  emailValidationFailed: Message;
  patternValidationFailed?: { [key: string]: Message };
  unauthorized: Message;
  formError: Message;
  requiredImages: Message;
  alreadyExists: Message;
}

export const defaultFormMessages: FormMessages = defineMessages({
  loading: { id: "default.loading", defaultMessage: "Loading..." },
  loadError: { id: "default.loadError", defaultMessage: "Load Error" },
  loadRetry: { id: "default.retry", defaultMessage: "Retry" },
  entityName: { id: "default.entity", defaultMessage: "Entity" },
  saveReminder: { id: "default.saveRemember", defaultMessage: "Remember to save your changes" },
  saveError: {
    id: "default.saveError",
    defaultMessage: "An error occured when saving your changes",
  },
  save: { id: "default.save", defaultMessage: "Save" },
  saving: { id: "default.saving", defaultMessage: "Saving..." },
  goBack: { id: "default.goBack", defaultMessage: "Go Back" },
  minLengthValidationFailed: {
    id: "default.validation.minLength",
    defaultMessage: "Must have at least {limit} characters.",
  },
  maxLengthValidationFailed: {
    id: "default.validation.maxLength",
    defaultMessage: "Must have no more than {limit} characters.",
  },
  requiredValidationFailed: {
    id: "default.validation.required",
    defaultMessage: "This field cannot be empty",
  },
  defaultPatternFailedMessage: {
    id: "default.validation.pattern",
    defaultMessage: "Must comply with pattern.",
  },
  unauthorized: {
    id: "default.unauthorized",
    defaultMessage: "You are not authorized to perform the requested action.",
  },
  formError: {
    id: "app.form.emptyFields",
    defaultMessage: "Form Error",
  },
  requiredImages: {
    id: "app.required.images",
    defaultMessage: "At least one image must be uploaded.",
  },
  emailValidationFailed: {
    id: "default.validation.email",
    defaultMessage: "emailValidationFailed",
  },
  alreadyExists: {
    id: "default.validation.alreadyExists",
    defaultMessage: "alreadyExists",
  },
});

interface FormProps<T extends Identifiable> {
  /**
   * The id of the entity being modified. If undefined, the form
   * is being used to create a new entity.
   */
  id?: string;
  validate?: (formData: T, errors: FormValidation) => FormValidation;

  formConfiguration: FormConfiguration<T>;

  onGet?(id: string): Promise<T>;

  onUpdate?(entity: T): Promise<void>;

  onCreate?(entity: T): Promise<T["id"]> | void;

  container?: React.ComponentType<PropsWithChildren<{}>>;
  schema: object;
  messages?: FormMessages;

  //Controls if the user can update or create
  canCreate?: IAuthzAction;
  canUpdate?: IAuthzAction;
  inSimpleCrud?: boolean;
  nestedData?: T[];
  nestedEntityName?: Message;
  updateParentForm?: (nestedFormData: T, id: string) => void;
  enumsOptional?: boolean;
  customOnChange?: (e: IChangeEvent<T>, es?: ErrorSchema) => T;
  showDirtyMessage?: boolean;
}

const FormDefaultContainer = styled("div")`
  input:not([type="radio"]) {
    width: 200px;
  }

  .fr-box {
    width: 750px;
  }

  .form-group {
    margin-bottom: 15px;
  }

  fieldset {
    margin-bottom: 0;
    padding-bottom: 0;
  }

  .error-detail {
    margin: 0;
    padding: 0;

    > li {
      font-size: 12px;
      list-style: none;
      color: #b20e0e;
      background-color: #f4dbdb;
      width: fit-content;
      padding: 3px 5px;
      margin-top: 4px;
      border-radius: 5px;
    }
  }

  .reminder {
    margin-top: 30px;
    padding: 12px 20px;
    background-color: #fff4e7;
    color: #ff9125;
    width: 100%;
    border-radius: 5px;
    font-weight: 600;
    display: flex;
    justify-content: left;
    margin-bottom: 10px;
  }
`;

const DirtyMessage = ({ dirty, message }: { dirty?: boolean; message: Message }) =>
  dirty ? (
    <div className="reminder">
      <FormattedMessage {...message} />
    </div>
  ) : null;

const ErrorDiv = styled.div`
  width: 100%;
`;

const BigText = styled.big`
  display: inline-block;
  width: 100%;
  text-align: center;
`;

const StyledButton = styled(Button)`
  background-color: ${APP_COLORS["gray-100"]};
  color: #212529;
`;

const ErrorMessage = ({ error, message }: { error?: string; message: Message }) =>
  error ? (
    <ErrorDiv>
      <Alert variant="danger">
        <strong>
          <FormattedMessage {...message} />
          &nbsp;
          {error}
        </strong>
      </Alert>
    </ErrorDiv>
  ) : null;

const FormErrorMessage = () => (
  <ErrorDiv>
    <Alert variant="danger">
      <strong>
        <FormattedMessage {...defaultFormMessages.formError} />
      </strong>
    </Alert>
  </ErrorDiv>
);

function getEnumNamesAndValues<T extends { enumNames: string[]; enumValues: number[] }>(
  enumNamesAndValues: any[]
) {
  const results = { enumNames: [] as string[], enumValues: [] as number[] } as T;
  let index = 0;
  while (enumNamesAndValues.length > index) {
    const value = enumNamesAndValues[index];
    if (isNaN(value)) {
      if (!value.endsWith("_INVALID")) {
        results.enumNames.push(value);
      } else if (!isNaN(enumNamesAndValues[index + 1])) {
        index++; // if the next value is a number, it is ignored as well
      }
    } else {
      results.enumValues.push(value);
    }
    index++;
  }
  return results;
}

function DefaultFormContainer(props: any) {
  return <>{props.children}</>;
}

export default function CrudForm<T extends Identifiable>({
  id,
  formConfiguration,
  onGet,
  onCreate,
  onUpdate,
  canCreate,
  canUpdate,
  schema,
  messages = defaultFormMessages,
  container = DefaultFormContainer,
  validate,
  inSimpleCrud,
  nestedData,
  updateParentForm,
  enumsOptional,
  customOnChange,
  showDirtyMessage = true,
}: FormProps<T>) {
  const intl = useIntl();
  const history = useHistory();
  const [{ loading, loadError, data }, loadDispatch] = useLoaderState<T>();
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState<boolean>(false);
  const [authorized, setAuthorized] = useState(true);
  const [saveError, setSaveError] = useState<string | undefined>(undefined);
  const [formError, setFormError] = useState<boolean | undefined>(false);
  const navigation = useCrudNavigation();
  const authorizer = useAuthorizer();

  const applyTransformations = useMemo(
    () => (input: T): T => {
      function applyTransform<V>(input: V, config: FormConfigurationFields<V>): V {
        for (const key in input) {
          const c: any = config[key];
          if (c?.transform) {
            input[key] = c.transform(input[key]) as V[Extract<keyof V, string>];
          } else if (c?.children && !(input instanceof Array)) {
            applyTransform(input[key], c.children);
          }
        }
        return input;
      }

      return applyTransform(input, formConfiguration);
    },
    [formConfiguration]
  );

  const transformErrors = useCallback(
    (errors: AjvError[]) => {
      return errors.map(error => {
        switch (error.name) {
          case "pattern":
            const patternMessage = messages?.patternValidationFailed?.[error.property];
            error.message = intl.formatMessage(
              patternMessage || messages.defaultPatternFailedMessage,
              error.params
            );
            break;
          case "required":
            error.message = intl.formatMessage(messages?.requiredValidationFailed, error.params);
            break;
          case "minLength":
            error.message = intl.formatMessage(messages?.minLengthValidationFailed, error.params);
            break;
          case "maxLength":
            error.message = intl.formatMessage(messages?.maxLengthValidationFailed, error.params);
            break;
        }
        return error;
      });
    },
    [intl, messages]
  );

  const onChange = useCallback(
    (e: IChangeEvent<T>, es?: ErrorSchema) => {
      if (!nestedData) {
        if (!e.errors.length) {
          setFormError(false);
        }
        if (id) {
          loadDispatch({ type: "load_success", data: e.formData });
        }
        setDirty(true);
        if (customOnChange) {
          const newData = customOnChange(e, es);
          loadDispatch({ type: "load_success", data: newData });
        }
      }
    },
    [customOnChange, id, loadDispatch, nestedData]
  );
  const handleUpdateParentForm = useCallback(
    (childFormData: any, fieldName, id: string) => {
      const dat = cloneDeep(data);
      if (id && dat && Array.isArray(dat[fieldName])) {
        const toDeleteIndex =
          dat &&
          Array.isArray(dat[fieldName]) &&
          dat[fieldName].findIndex((it: any) => it.id === id);
        dat[fieldName].splice(toDeleteIndex, 1);
      }
      dat && dat[fieldName].push(childFormData);
      loadDispatch({ type: "load_success", data: dat });
    },
    [data, loadDispatch]
  );

  const handleDeleteChildForm = useCallback(
    (id: string, fieldName) => {
      const dat = cloneDeep(data);
      if (dat && Array.isArray(dat[fieldName])) {
        const toDeleteIndex =
          dat &&
          Array.isArray(dat[fieldName]) &&
          dat[fieldName].findIndex((it: any) => it.id === id);
        dat[fieldName].splice(toDeleteIndex, 1);
        loadDispatch({ type: "load_success", data: dat });
      }
    },
    [data, loadDispatch]
  );

  const load = useMemo(
    () =>
      async function (loadId: string) {
        try {
          loadDispatch({ type: "load_start" });
          const data = nestedData
            ? nestedData.find(it => it.id === loadId)
            : typeof onGet === "function" && (await onGet(loadId));
          if (data) {
            loadDispatch({
              type: "load_success",
              data: applyTransformations(data),
            });
          }
        } catch (e) {
          console.error("Error loading entity", e);
          loadDispatch({ type: "load_error", errorMessageId: "serviceError" });
        }
      },
    [onGet, applyTransformations, loadDispatch, nestedData]
  );

  const reload = useCallback(() => (id ? load(id) : null), [load, id]);

  useEffect(() => {
    if (id) {
      if (canUpdate && !authorizer.canExecute(canUpdate)) {
        console.error("You are not authorized to update this entity.");
        setAuthorized(false);
        return;
      }
      const preloadKey = `preloaded_${id}`;
      const rawData = window.sessionStorage.getItem(preloadKey);
      //Load data from session storage if available
      if (rawData) {
        loadDispatch({
          type: "load_success",
          data: applyTransformations(JSON.parse(rawData)),
        });
        window.sessionStorage.removeItem(preloadKey);
      } else {
        load(id);
      }
    } else {
      if (canCreate && !authorizer.canExecute(canCreate)) {
        console.error("You are not authorized to create a new entity.");
        setAuthorized(false);
        return;
      }
      loadDispatch({ type: "load_success", data: undefined });
    }
  }, [id, load, loadDispatch, applyTransformations, authorizer, canCreate, canUpdate]);

  const [processedSchema, fields, uiSchema] = useMemo(() => {
    function process<E>(
      schema: JSONSchema6,
      fields: { [key: string]: Field },
      config?: FormConfiguration<E>
    ): [JSONSchema6, { [key: string]: Field }, UiSchema] {
      //Go through the fields
      const uiSchema: { [key: string]: any } = {};
      // Prevents add fields button from appearing
      uiSchema["ui:options"] = { expandable: false };
      //If there's an ordering, apply it.
      if (config?.__fieldOrder) {
        uiSchema["ui:order"] = config.__fieldOrder;
      }

      Object.entries(schema.properties || schema).forEach(([k, v]) => {
        if (typeof v !== "boolean") {
          //Append `List` suffix to the field name if the value is an array.
          const fieldName = (v.type === "array" && config?.__useProtoStyleArrays
            ? `${k}List`
            : k) as keyof E & string;

          const fieldConfig = config ? config[fieldName] : undefined;
          if (fieldConfig?.placeholder) {
            uiSchema[fieldName] = {
              "ui:placeholder": intl.formatMessage(fieldConfig?.placeholder),
              ...uiSchema[fieldName],
            };
          }

          let labelMessage: Message | undefined = undefined;
          const hiddenValue =
            typeof fieldConfig?.hidden === "function"
              ? fieldConfig?.hidden(!id)
              : fieldConfig?.hidden;
          if (!Array.isArray(fieldConfig?.messages?.label)) {
            labelMessage = fieldConfig?.messages?.label;
          }
          v.title = fieldName;
          if (fieldConfig?.hideTitle) {
            v.title = "";
          } else if (!!labelMessage) {
            v.title = intl.formatMessage(labelMessage);
          }
          if (fieldConfig?.messages?.description) {
            v.description = intl.formatMessage(fieldConfig?.messages?.description);
          }

          if (fieldConfig?.default) {
            const value =
              fieldConfig.default instanceof Function ? fieldConfig.default() : fieldConfig.default;
            v.default = value as any;
          }
          if (v.type === "array") {
            //We need to rename it to ...List
            //protoc-gen-jsonschema generates the names without the List suffix.
            if (schema.properties && config?.__useProtoStyleArrays) {
              // @ts-ignore
              delete schema.properties[k];
              schema.properties[k + "List"] = v;
            }
            if (fieldConfig?.hidden) {
              if (hiddenValue) {
                uiSchema[fieldName] = {
                  "ui:ArrayFieldTemplate": "hidden",
                  ...uiSchema[fieldName],
                };
              }
            }
            if (
              fieldConfig?.children &&
              v.items &&
              typeof v.items !== "boolean" &&
              !(v.items instanceof Array)
            ) {
              const [, , childUiSchema] = process(
                v.items,
                fields,
                fieldConfig.children as FormConfiguration<any>
              );

              uiSchema[fieldName] = {
                items: childUiSchema,
              };
            }
          }
          if (v.type === "object") {
            if (fieldConfig?.hidden) {
              if (hiddenValue) {
                uiSchema[fieldName] = {
                  "ui:FieldTemplate": "hidden",
                  ...uiSchema[fieldName],
                };
              }
            }
          }
          if (v.type === "object" && v.properties) {
            if (
              Array.isArray(fieldConfig?.messages?.label) &&
              fieldConfig?.messages?.label.length === Object.keys(v.properties).length
            ) {
              Object.keys(v.properties).forEach((property, index) => {
                if (v.properties && Object.keys(v.properties).length) {
                  (v.properties[property] as JSONSchema6).title = intl.formatMessage(
                    (fieldConfig?.messages?.label as Array<Message>)[index]
                  );
                }
              });
            } else if (fieldConfig?.children && Object.keys(fieldConfig?.children).length) {
              Object.keys(v.properties).forEach(property => {
                if (v.properties && Object.keys(v.properties).length && fieldConfig.children) {
                  (v.properties[property] as JSONSchema6).title = intl.formatMessage(
                    // @ts-ignore
                    (fieldConfig.children[property] as FormFieldConfiguration<T>).messages
                      ?.label as Message
                  );
                }
              });
            }

            if (fieldConfig?.children && v.properties) {
              const [, , childUiSchema] = process(
                v.properties,
                fields,
                fieldConfig.children as FormConfiguration<any>
              );
              uiSchema[fieldName] = {
                ...childUiSchema,
              };
            }
          }

          if (v.enum) {
            if (!enumsOptional) {
              schema.required?.push(k);
            }
            const { enumNames, enumValues } = getEnumNamesAndValues(v.enum);
            v.enum = enumValues.length ? enumValues : enumNames;
            if (fieldConfig?.enumLabels) {
              if (v.enum.length !== fieldConfig?.enumLabels?.length) {
                throw new Error("enum and enumNames length must match.");
              }
              //@ts-ignore
              v.enumNames = fieldConfig?.enumLabels?.map(v => intl.formatMessage(v));
            }
          }
          if (fieldConfig?.widget) {
            uiSchema[fieldName] = { "ui:widget": fieldConfig.widget };
          }

          if (fieldConfig?.fieldWidget) {
            uiSchema[fieldName] = { "ui:field": fieldConfig.fieldWidget };
          }
          if (
            fieldConfig?.nestedFormConfiguration &&
            fieldConfig?.nestedTableColumns &&
            data &&
            Array.isArray(data[fieldName])
          ) {
            uiSchema[fieldName] = {
              "ui:field": (props: any) => {
                const nestedSchema = {
                  ...cloneDeep(schema),
                  properties:
                    schema?.properties &&
                    schema?.properties[fieldName] &&
                    //@ts-ignore
                    schema?.properties[fieldName]?.items?.properties,
                };
                // @ts-ignore
                delete nestedSchema.required;
                nestedSchema.properties = {
                  ...nestedSchema.properties,
                  id: {
                    type: "string",
                  },
                };
                const propName = fieldName;
                type NestedType = T[typeof propName];

                const nestedData = data && (data[fieldName] as NestedType[]);
                nestedData.forEach((it: any) => {
                  if (typeof it === "object") {
                    it.id = uuidv4();
                  }
                });
                const formConfiguration = cloneDeep(fieldConfig?.nestedFormConfiguration);
                return (
                  <SimpleCrud<NestedType>
                    formConfiguration={
                      formConfiguration as FormConfiguration<unknown & Identifiable>
                    }
                    tableColumns={fieldConfig?.nestedTableColumns as TableColumns<unknown>}
                    schema={nestedSchema}
                    nestedData={nestedData}
                    updateParentForm={(nestedFormData: unknown, id: string) =>
                      handleUpdateParentForm(nestedFormData, fieldName, id)
                    }
                    deleteChildForm={(id: string) => handleDeleteChildForm(id, fieldName)}
                    nestedEntityName={fieldConfig.nestedEntityName}
                    messages={messages as CrudMessages}
                    validate={validate}
                    nestedFieldId={fieldConfig?.id}
                  />
                );
              },
            };
          }

          if (fieldConfig?.displayArrayAs) {
            uiSchema[fieldName] = {
              "ui:ArrayFieldTemplate": fieldConfig.displayArrayAs,
              ...uiSchema[fieldName],
            };
          } else if (fieldConfig?.hidden) {
            if (hiddenValue) {
              uiSchema[fieldName] = {
                "ui:widget": "hidden",
                ...uiSchema[fieldName],
              };
            }
          }
          if (fieldConfig?.readonly) {
            uiSchema[fieldName] = {
              "ui:readonly":
                typeof fieldConfig.readonly === "function"
                  ? fieldConfig.readonly(!id)
                  : fieldConfig.readonly,
              ...uiSchema[fieldName],
            };
          }
          if (fieldConfig?.messages?.helpText) {
            uiSchema[fieldName] = {
              "ui:help": (
                <div style={{ whiteSpace: "pre-line" }}>
                  {intl.formatMessage(fieldConfig?.messages?.helpText)}
                </div>
              ),
              ...uiSchema[fieldName],
            };
          }
        }
      });

      return [schema, fields, uiSchema];
    }

    const s = JSON.parse(JSON.stringify(schema)) as JSONSchema6;
    return process(s, {}, formConfiguration);
  }, [
    schema,
    formConfiguration,
    id,
    data,
    intl,
    enumsOptional,
    handleUpdateParentForm,
    handleDeleteChildForm,
    messages,
    validate,
  ]);
  const onSave = useCallback(
    async (e: ISubmitEvent<T>) => {
      loadDispatch({ type: "load_success", data: e.formData });
      setSaving(true);
      setSaveError(undefined);
      if (updateParentForm && nestedData) {
        updateParentForm(e.formData, String(e.formData.id));
        return;
      }
      if (id && onUpdate) {
        //Update
        try {
          await onUpdate(e.formData);
          setSaving(false);
          navigation.goToList(true);
        } catch (e) {
          console.log("Error updating entity", e);
          setSaving(false);
          //@ts-ignore
          setSaveError(e.message);
          setDirty(false);
        }
      } else if (onCreate) {
        //Create
        try {
          await onCreate(e.formData);
          setSaving(false);
          navigation.goToList(true);
        } catch (e) {
          console.log("Error saving entity", e);
          setSaving(false);
          //@ts-ignore
          setSaveError(e.message);
          setDirty(false);
        }
      }
    },
    [onCreate, onUpdate, id, navigation, loadDispatch, nestedData, updateParentForm]
  );

  const onBack = useCallback(() => (inSimpleCrud ? navigation.goToList() : history.goBack()), [
    navigation,
    history,
    inSimpleCrud,
  ]);
  const ModuleContainer = container;
  const onError = useCallback(errors => {
    console.error("form validation errors", errors);
    if (errors.length) {
      setFormError(true);
    }
  }, []);

  const form = useMemo(() => {
    return (
      <Form
        validate={validate}
        noHtml5Validate
        liveValidate
        schema={processedSchema}
        uiSchema={uiSchema}
        formData={data}
        disabled={saving}
        showErrorList={false}
        fields={fields}
        onSubmit={onSave}
        onChange={onChange}
        transformErrors={transformErrors}
        onError={onError}>
        <Button variant="primary" disabled={saving} type="submit">
          {!saving && <FormattedMessage {...messages?.save} />}
          {saving && (
            <>
              <FormattedMessage {...messages?.saving} />{" "}
              <FontAwesomeIcon icon={faCircleNotch} spin />
            </>
          )}
        </Button>
        <StyledButton className="mr-2 ml-1" variant="light" disabled={saving} onClick={onBack}>
          <FormattedMessage {...messages?.goBack} />
        </StyledButton>
      </Form>
    );
  }, [
    data,
    fields,
    onChange,
    onError,
    onSave,
    processedSchema,
    saving,
    transformErrors,
    uiSchema,
    validate,
    messages,
    onBack,
  ]);
  return (
    <ContentWrapper>
      <SectionHeading>
        <FormattedMessage {...messages?.entityName} />
      </SectionHeading>
      <Card className="ml-4 mr-4 mb-4 mt-4">
        <Card.Body>
          {!authorized && <FormattedMessage {...messages?.unauthorized} />}
          {authorized && (
            <>
              {loading && (
                <BigText className="text-muted">
                  <FormattedMessage {...messages?.loading} />.<br />
                  <FontAwesomeIcon icon={faSyncAlt} spin />
                </BigText>
              )}
              {!loading && loadError && (
                <BigText className="text-muted">
                  <FormattedMessage {...messages?.loadError} />
                  <br />
                  <Button size="sm" onClick={reload}>
                    <FormattedMessage {...messages?.loadRetry} />
                  </Button>
                </BigText>
              )}
              {!loading && !loadError && (
                <FormDefaultContainer>
                  <ModuleContainer>
                    {form}
                    {showDirtyMessage && (
                      <DirtyMessage dirty={dirty} message={messages?.saveReminder} />
                    )}
                    <ErrorMessage error={saveError} message={messages?.saveError} />
                    {formError && <FormErrorMessage />}
                  </ModuleContainer>
                </FormDefaultContainer>
              )}
            </>
          )}
        </Card.Body>
      </Card>
    </ContentWrapper>
  );
}
